import { isAuthorized, usePermissions } from '@luminovo/auth';
import * as Sentry from '@sentry/react';
import React from 'react';

import { BomImporterProvider } from '@/areas/bom-importer/BomImporter/BomImporterContext';
import { useCurrentUserDetailsContext } from '@/shared/data/contexts/CurrentUserDetailsContext';
import { useTenantDetailsContext } from '@/shared/data/contexts/TenantDetailsContext';
import { MANUFACTURING_PAID_PERMISSIONS } from '@/shared/permissions/featureFlags';
import { useSession } from '@/shared/permissions/session';
import { ErrorBoundaryClearedOnNavigation } from '@/shared/ui/errors/ErrorBoundaryClearedOnNavigation';
import { initializeServices, useOnCookieConsent } from '@/shared/utils/services';

import { reportAuthBootstrapMilestone } from './authBootstrapTelemetry';
import { MagicBellConnection } from './MagicBell/MagicBellConnection';

/** The signed-in shell around the page: session services and notifications. */
function AuthenticatedApp({ children }: { children: React.ReactNode }) {
    const { user, organization, customer, userType } = useCurrentUserDetailsContext();
    const consentedCookies = useOnCookieConsent();
    const { permissions } = usePermissions();
    const { tenantDetails } = useTenantDetailsContext();
    const session = useSession();

    // The auth bootstrap ends here. The gap from `sdk_ready` is `getAccessTokenSilently`, then what `_authenticated`'s
    // `beforeLoad` waits for (the session's round trip, the user's language catalog), then the page's chunk.
    React.useEffect(() => {
        reportAuthBootstrapMilestone('app_interactive');
    }, []);

    React.useEffect(() => {
        initializeServices({
            user,
            organization,
            customer,
            userType,
            consentedCookies,
            hasFullManufacturingAccess: isAuthorized(permissions, MANUFACTURING_PAID_PERMISSIONS),
            hasAccessToSourceToContract: session.isFeatureFlagEnabled('negotiations'),
            tenantName: tenantDetails.tenant,
        });
        // re-run this effect only when the consented cookies change
    }, [consentedCookies, tenantDetails]);

    return (
        <ErrorBoundaryClearedOnNavigation>
            {/* Notifications are non-critical, never let them take down the app */}
            <Sentry.ErrorBoundary fallback={<></>}>
                <MagicBellConnection />
            </Sentry.ErrorBoundary>
            <BomImporterProvider>{children}</BomImporterProvider>
        </ErrorBoundaryClearedOnNavigation>
    );
}

export default Sentry.withProfiler(AuthenticatedApp);
